import { reactive, computed } from 'vue'

const state = reactive({
  access: null,
  refresh: null,
  initialized: false,
  username: null,
})

let storageListenerAttached = false

const decodeBase64UrlJson = (encoded) => {
  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const decoded = atob(padded)
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

const isAccessTokenExpired = (token) => {
  const payload = decodeBase64UrlJson(String(token || '').split('.')[1] || '')
  const expiresAtSeconds = Number(payload?.exp)
  return Number.isFinite(expiresAtSeconds) && Date.now() >= expiresAtSeconds * 1000
}

const storage = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage
  }
  if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
    return globalThis.localStorage
  }
  return null
}

export function useAuthStore() {
  const isAuthenticated = computed(() => (
    !!state.access && !isAccessTokenExpired(state.access)
  ))

  const _setAuthCookie = (enabled) => {
    if (typeof document === 'undefined') return
    const secureAttribute = typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : ''
    if (enabled) {
      document.cookie = `spa_auth=1; path=/; SameSite=Lax${secureAttribute}`
    } else {
      document.cookie = `spa_auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secureAttribute}`
    }
  }

  const syncFromStorage = () => {
    const ls = storage()
    if (!ls) return
    state.access = ls.getItem('access')
    state.refresh = ls.getItem('refresh')
    state.username = ls.getItem('username')
    _setAuthCookie(!!state.access)
    state.initialized = true
  }

  const _ensureStorageListener = () => {
    if (storageListenerAttached) return
    if (typeof window === 'undefined' || !window.addEventListener) return
    window.addEventListener('storage', (evt) => {
      if (!evt.key || ['access', 'refresh', 'username'].includes(evt.key)) {
        syncFromStorage()
      }
    })
    storageListenerAttached = true
  }

  const setTokens = (access, refresh, username = null) => {
    state.access = access || null
    state.refresh = refresh || null
    state.username = username || null
    const ls = storage()
    if (ls) {
      if (access) ls.setItem('access', access)
      if (refresh) ls.setItem('refresh', refresh)
      if (username) ls.setItem('username', username)
    }
    _setAuthCookie(!!access)
  }

  const clearTokens = () => {
    state.access = null
    state.refresh = null
    state.username = null
    const ls = storage()
    if (ls) {
      ls.removeItem('access')
      ls.removeItem('refresh')
      ls.removeItem('username')
    }
    _setAuthCookie(false)
  }

  const initFromStorage = () => {
    syncFromStorage()
    _ensureStorageListener()
  }

  return {
    state,
    isAuthenticated,
    setTokens,
    clearTokens,
    initFromStorage,
  }
}
